"use client";
import * as Yup from "yup";

import {
  InsuredParty,
  type TravelCoveredPerson,
  type TravelFamilyMember,
  type TravelQuote,
} from "@/api_client";
import type { TravelEntryContent } from "@/services/cms/contentTypes/entry/TravelEntryContent";
import { getErrorMessageNew } from "@/utils/errorMessage";

import { AgeValidator } from "../../shared/ageValidators";
import { validSSN } from "../../shared/ssnValidator";

type ValidationMessage = {
  Key?: string | null;
  Value?: string | null;
};

const getValidationMessage = (
  messages: (ValidationMessage | null)[] | null | undefined,
  errorKey: string,
) => {
  const defaultError = `Missing error for: ${errorKey}`;
  if (!messages) {
    return defaultError;
  }

  return messages.find((item) => item?.Key === errorKey)?.Value ?? defaultError;
};

const buildFamilyMemberSchema = (content?: TravelEntryContent | null) =>
  Yup.object<
    Omit<
      TravelFamilyMember,
      "firstName" | "lastName" | "streetAddress" | "municipality"
    >
  >({
    ssn: Yup.string()
      .required(
        getErrorMessageNew(
          content?.familyMemberSsn?.ValidationMessages,
          "required",
        ),
      )
      .test(
        "valid-ssn",
        getErrorMessageNew(
          content?.familyMemberSsn?.ValidationMessages,
          "valid-ssn",
        ) ?? "",
        (value) => validSSN(value),
      ),
  });

const buildCoveredPersonSchema = (content?: TravelEntryContent | null) =>
  Yup.object<
    Omit<TravelCoveredPerson, "postalCode" | "permanentResidentOfFinland">
  >({
    insuredParty: Yup.mixed<InsuredParty>()
      .required(
        getValidationMessage(
          content?.insuredParty?.ValidationMessages,
          "required",
        ),
      )
      .oneOf([InsuredParty.POLICY_HOLDER, InsuredParty.FAMILY_MEMBER]),
    familyMember: Yup.object().when("insuredParty", {
      is: InsuredParty.FAMILY_MEMBER,
      then: () =>
        buildFamilyMemberSchema(content).required(
          getValidationMessage(
            content?.insuredParty?.ValidationMessages,
            "required",
          ),
        ),
      otherwise: () => Yup.object().optional(),
    }),
  });

// Form only deals with a minimal subset of ITravel
export type TravelEntryFormValues = Pick<
  TravelQuote,
  "productType" | "coveredPerson" | "policyHolderSsn"
>;

export const TravelEntryValidationSchema = (
  content?: TravelEntryContent | null,
): Yup.ObjectSchema<TravelEntryFormValues> => {
  const schema: Yup.ObjectSchema<TravelEntryFormValues> = Yup.object({
    coveredPerson: buildCoveredPersonSchema(content).required(),
    policyHolderSsn: Yup.string()
      .required(
        getErrorMessageNew(
          content?.policyHolderSsn?.ValidationMessages,
          "required",
        ),
      )
      .test(
        "valid-ssn",
        getErrorMessageNew(
          content?.policyHolderSsn?.ValidationMessages,
          "valid-ssn",
        ) ?? "",
        validSSN,
      )
      .test(
        "is-18+",
        getErrorMessageNew(
          content?.policyHolderSsn?.ValidationMessages,
          "is-18+",
        ) ?? "",
        (ssn: string) => {
          const ageValidator = new AgeValidator(ssn);
          return ageValidator.isAtLeast18;
        },
      ),
  }).test(
    "coveredPerson.familyMember.ssn",
    getErrorMessageNew(
      content?.familyMemberSsn?.ValidationMessages,
      "required",
    ) ?? "",
    (value: Partial<TravelQuote>, context) => {
      if (value?.coveredPerson?.familyMember?.ssn === value?.policyHolderSsn) {
        return context.createError({
          path: "coveredPerson.familyMember.ssn",
          message:
            getErrorMessageNew(
              content?.familyMemberSsn?.ValidationMessages,
              "duplicate-ssn",
            ) ?? "",
        });
      }
      return true;
    },
  ) as Yup.ObjectSchema<TravelEntryFormValues>;
  return schema;
};
